import { useQuery } from '@tanstack/vue-query';
import { computed } from 'vue';
import { getRecentResolvedTicket } from '@/common/userRequest';
import { useBotUser } from '@/stores/useBotUser';
import { storeToRefs } from 'pinia';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { devLog } from '@/common/debug';

/**
 * TanStack Query for fetching recent resolved ticket
 * Automatically re-fetches when user authentication state changes
 */
export function useRecentResolvedTicketQuery() {
  const botUserStore = useBotUser();
  const { user, isAuthenticated } = storeToRefs(botUserStore);

  const mockedFlowStore = useMockedFlowStore();
  const {
    isActive: isMockedFlowActive,
    hasRecentResolvedTicket: hasMockedRecentResolvedTicket,
    recentResolvedTicket: mockedRecentResolvedTicket,
  } = storeToRefs(mockedFlowStore);

  const userId = computed(() => (isAuthenticated.value ? user.value.id : null));

  const {
    data: ticketData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['recentResolvedTicket', userId],
    queryFn: async () => {
      // Check if we're in mocked mode and have mocked recent resolved ticket
      if (isMockedFlowActive.value) {
        if (!hasMockedRecentResolvedTicket.value || !mockedRecentResolvedTicket.value) {
          return null;
        }

        devLog.debug('[RECENT_RESOLVED_TICKET] Using mocked recent resolved ticket data');
        const data = mockedRecentResolvedTicket.value;

        // Return null if no ticket or if ticket has reviews (don't show if already reviewed)
        if (!data || !data.ticket || data.ticketReviews.length > 0) {
          return null;
        }

        return data.ticket;
      }

      if (!userId.value) {
        return null;
      }

      const data = await getRecentResolvedTicket(userId.value);

      // Return null if no ticket or if ticket has reviews (don't show if already reviewed)
      if (!data || !data.ticket || data.ticketReviews.length > 0) {
        return null;
      }

      return data.ticket;
    },
    enabled: computed(() => isAuthenticated.value && !!userId.value),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  return {
    ticket: ticketData,
    isLoading,
    isError,
    error,
    refetch,
  };
}
